import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  Clock, 
  Eye,
  MousePointer,
  Crown,
  CalendarIcon,
  ArrowRight,
  Loader2,
  GitCompare,
  Star,
  Gift,
  Users,
  Timer,
  Download,
  FileSpreadsheet,
  Lock
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const Analytics = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // New analytics states
  const [reviewStats, setReviewStats] = useState(null);
  const [behaviorStats, setBehaviorStats] = useState(null);
  const [promoStats, setPromoStats] = useState(null);
  const [trendsStats, setTrendsStats] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Period comparison state
  const [restaurant, setRestaurant] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [period1, setPeriod1] = useState({
    from: subDays(new Date(), 7),
    to: new Date()
  });
  const [period2, setPeriod2] = useState({
    from: subDays(new Date(), 14),
    to: subDays(new Date(), 8)
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, detailRes, restRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/analytics/detailed'),
        api.get('/restaurant')
      ]);
      setDashboardStats(dashRes.data);
      setDetailedStats(detailRes.data);
      setRestaurant(restRes.data);
      
      // Fetch advanced analytics
      const [reviewRes, behaviorRes, promoRes, trendsRes] = await Promise.all([
        api.get('/analytics/reviews').catch(() => ({ data: null })),
        api.get('/analytics/behavior').catch(() => ({ data: null })),
        api.get('/analytics/promotions').catch(() => ({ data: null })),
        api.get('/analytics/trends').catch(() => ({ data: null }))
      ]);
      
      setReviewStats(reviewRes.data);
      setBehaviorStats(behaviorRes.data);
      setPromoStats(promoRes.data);
      setTrendsStats(trendsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    if (restaurant?.subscription_plan !== 'premium') {
      toast.error('Cette fonctionnalité nécessite un abonnement Premium');
      return;
    }
    
    setComparisonLoading(true);
    try {
      const response = await api.get('/analytics/compare', {
        params: {
          period1_start: format(period1.from, 'yyyy-MM-dd'),
          period1_end: format(period1.to, 'yyyy-MM-dd'),
          period2_start: format(period2.from, 'yyyy-MM-dd'),
          period2_end: format(period2.to, 'yyyy-MM-dd')
        }
      });
      setComparisonData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la comparaison');
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleExport = async (exportFormat) => {
    setExportLoading(true);
    try {
      if (exportFormat === 'csv') {
        const response = await api.get('/analytics/export', {
          params: { format: 'csv' },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics_${format(new Date(), 'yyyyMMdd')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export CSV téléchargé');
      } else {
        const response = await api.get('/analytics/export', {
          params: { format: 'json' }
        });
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics_${new Date().toISOString().slice(0,10)}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Export JSON téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  const COLORS = ['#F97316', '#10B981', '#3B82F6', '#8B5CF6', '#F43F5E', '#06B6D4', '#84CC16'];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const dailyData = detailedStats?.daily_stats?.map(day => ({
    date: day._id?.slice(5) || 'N/A',
    vues: day.views || 0,
    clics: day.clicks || 0,
    scans: day.scans || 0
  })) || [];

  const dishData = detailedStats?.popular_dishes?.slice(0, 5).map(dish => ({
    name: dish.name?.length > 15 ? dish.name.slice(0, 15) + '...' : dish.name,
    vues: dish.views || 0,
    clics: dish.clicks || 0
  })) || [];

  const peakHoursData = detailedStats?.peak_hours?.map(hour => ({
    heure: `${hour._id}h`,
    visites: hour.count || 0
  })) || [];

  // Format time duration
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-orange-500" />
            Analytics
          </h1>
          <p className="text-gray-500 mt-1">
            Analysez les performances de votre menu
          </p>
        </div>
        
        {/* Export Buttons - Essentiel+ only */}
        {restaurant?.subscription_plan !== 'starter' ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exportLoading}
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('json')}
              disabled={exportLoading}
              data-testid="export-json-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
          </div>
        ) : (
          <Badge className="bg-gray-100 text-gray-600">
            <Lock className="h-3 w-3 mr-1" />
            Export (Essentiel+)
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="overview" className="text-xs sm:text-sm">
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="reviews" className="text-xs sm:text-sm" disabled={restaurant?.subscription_plan === 'starter'}>
            <Star className="h-3 w-3 mr-1 hidden sm:inline" />
            Avis
            {restaurant?.subscription_plan === 'starter' && <Lock className="h-3 w-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="behavior" className="text-xs sm:text-sm" disabled={restaurant?.subscription_plan === 'starter'}>
            <Users className="h-3 w-3 mr-1 hidden sm:inline" />
            Comportement
            {restaurant?.subscription_plan === 'starter' && <Lock className="h-3 w-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="promotions" className="text-xs sm:text-sm" disabled={restaurant?.subscription_plan === 'starter'}>
            <Gift className="h-3 w-3 mr-1 hidden sm:inline" />
            Promotions
            {restaurant?.subscription_plan === 'starter' && <Lock className="h-3 w-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="trends" className="text-xs sm:text-sm" disabled={restaurant?.subscription_plan === 'starter'}>
            <TrendingUp className="h-3 w-3 mr-1 hidden sm:inline" />
            Tendances
            {restaurant?.subscription_plan === 'starter' && <Lock className="h-3 w-3 ml-1" />}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Scans ce mois</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats?.scans?.month || 0}</p>
                    <p className="text-xs text-emerald-500 flex items-center gap-1 mt-1">
                      <TrendingUp className="h-3 w-3" />
                      {dashboardStats?.scans?.week || 0} cette semaine
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-orange-50 text-orange-500">
                    <Eye className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Plats au menu</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats?.total_dishes || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">plats disponibles</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                    <MousePointer className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Panier estimé</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardStats?.estimated_basket || 0}€</p>
                    <p className="text-xs text-gray-400 mt-1">moyenne estimée</p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50 text-emerald-500">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Daily Activity */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Activité des 7 derniers jours</CardTitle>
              </CardHeader>
              <CardContent>
                {dailyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }} 
                      />
                      <Legend />
                      <Line type="monotone" dataKey="scans" name="Scans QR" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="vues" name="Vues plats" stroke="#F97316" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="clics" name="Clics" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-gray-400">
                    Pas encore de données
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Peak Hours */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-purple-500" />
                  Heures de pointe
                </CardTitle>
              </CardHeader>
              <CardContent>
                {peakHoursData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={peakHoursData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="heure" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }} 
                      />
                      <Bar dataKey="visites" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-gray-400">
                    Les données s'afficheront après quelques visites
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="mt-6 space-y-6">
          <div className="grid sm:grid-cols-4 gap-4">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Clics Google</p>
                    <p className="text-3xl font-bold text-gray-900">{reviewStats?.clicks?.google || 0}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Clics Tripadvisor</p>
                    <p className="text-3xl font-bold text-gray-900">{reviewStats?.clicks?.tripadvisor || 0}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50">
                    <div className="w-6 h-6 rounded-full bg-[#34E0A1] flex items-center justify-center">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="white">
                        <circle cx="8" cy="12" r="2.5"/>
                        <circle cx="16" cy="12" r="2.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Clics bannière</p>
                    <p className="text-3xl font-bold text-gray-900">{reviewStats?.clicks?.banner || 0}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-amber-50 text-amber-500">
                    <Star className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Taux conversion</p>
                    <p className="text-3xl font-bold text-gray-900">{reviewStats?.conversion_rate || 0}%</p>
                    <p className="text-xs text-gray-400 mt-1">visiteurs → avis</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-50 text-purple-500">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Review clicks chart */}
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Clics sur les avis (30 derniers jours)</CardTitle>
            </CardHeader>
            <CardContent>
              {reviewStats?.daily_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={reviewStats.daily_stats.map(d => ({
                    date: d._id?.slice(5) || '',
                    google: d.google || 0,
                    tripadvisor: d.tripadvisor || 0,
                    banner: d.banner || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="google" name="Google" stackId="1" stroke="#4285F4" fill="#4285F4" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="tripadvisor" name="Tripadvisor" stackId="1" stroke="#34E0A1" fill="#34E0A1" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="banner" name="Bannière" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-gray-400">
                  Les données apparaîtront après les premiers clics sur les liens d'avis
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Behavior Tab */}
        <TabsContent value="behavior" className="mt-6 space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Temps moyen sur le menu</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatDuration(behaviorStats?.avg_session_duration || 0)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{behaviorStats?.total_sessions || 0} sessions</p>
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50 text-blue-500">
                    <Timer className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Taux de rebond</p>
                    <p className="text-3xl font-bold text-gray-900">{behaviorStats?.bounce_rate || 0}%</p>
                    <p className="text-xs text-gray-400 mt-1">visiteurs sans interaction</p>
                  </div>
                  <div className={`p-3 rounded-xl ${(behaviorStats?.bounce_rate || 0) > 50 ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'}`}>
                    {(behaviorStats?.bounce_rate || 0) > 50 ? (
                      <TrendingDown className="h-6 w-6" />
                    ) : (
                      <TrendingUp className="h-6 w-6" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Catégories vues</p>
                    <p className="text-3xl font-bold text-gray-900">{behaviorStats?.category_views?.length || 0}</p>
                    <p className="text-xs text-gray-400 mt-1">catégories actives</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-50 text-purple-500">
                    <Eye className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Category Views */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Catégories les plus consultées</CardTitle>
              </CardHeader>
              <CardContent>
                {behaviorStats?.category_views?.length > 0 ? (
                  <div className="space-y-3">
                    {behaviorStats.category_views.map((cat, index) => (
                      <div key={cat._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          >
                            {index + 1}
                          </div>
                          <span className="font-medium text-gray-900">{cat._id}</span>
                        </div>
                        <Badge variant="secondary">{cat.views} vues</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-gray-400">
                    Données en cours de collecte
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Distribution */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Visites par jour de la semaine</CardTitle>
              </CardHeader>
              <CardContent>
                {behaviorStats?.daily_distribution && Object.keys(behaviorStats.daily_distribution).length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={Object.entries(behaviorStats.daily_distribution).map(([day, count]) => ({
                      jour: day,
                      visites: count
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="jour" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="visites" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-400">
                    Données en cours de collecte
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Promotions Tab */}
        <TabsContent value="promotions" className="mt-6 space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Vues promotions</p>
                    <p className="text-3xl font-bold text-gray-900">{promoStats?.totals?.views || 0}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-orange-50 text-orange-500">
                    <Eye className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Clics promotions</p>
                    <p className="text-3xl font-bold text-gray-900">{promoStats?.totals?.clicks || 0}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50 text-emerald-500">
                    <MousePointer className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Taux d'engagement</p>
                    <p className="text-3xl font-bold text-gray-900">{promoStats?.totals?.engagement_rate || 0}%</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-50 text-purple-500">
                    <Gift className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Individual promotions */}
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Performance par promotion</CardTitle>
            </CardHeader>
            <CardContent>
              {promoStats?.promotions?.length > 0 ? (
                <div className="space-y-4">
                  {promoStats.promotions.map((promo, index) => (
                    <div key={promo.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                          >
                            <Gift className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{promo.name}</p>
                          </div>
                        </div>
                        <Badge 
                          className={promo.engagement_rate > 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}
                        >
                          {promo.engagement_rate}% engagement
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">Vues</p>
                          <p className="text-xl font-bold text-gray-900">{promo.views}</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-500">Clics</p>
                          <p className="text-xl font-bold text-gray-900">{promo.clicks}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-400 text-center">
                  <div>
                    <Gift className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucune donnée de promotion</p>
                    <p className="text-sm">Créez des promotions pour voir les statistiques</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-6 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Weekly Trend */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Évolution hebdomadaire</CardTitle>
                <CardDescription>Scans QR sur les 8 dernières semaines</CardDescription>
              </CardHeader>
              <CardContent>
                {trendsStats?.weekly_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trendsStats.weekly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        labelFormatter={(label) => {
                          const item = trendsStats.weekly_trend.find(w => w.week === label);
                          return item ? `Semaine du ${item.week_start}` : label;
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="scans" 
                        name="Scans"
                        stroke="#F97316" 
                        fill="#F97316" 
                        fillOpacity={0.3} 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">
                    Pas assez de données pour afficher les tendances
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Day Comparison */}
            <Card className="border-0 shadow-soft">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comparaison par jour</CardTitle>
                <CardDescription>Performance moyenne par jour de la semaine</CardDescription>
              </CardHeader>
              <CardContent>
                {trendsStats?.day_comparison?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={trendsStats.day_comparison}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="scans" name="Scans" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                        {trendsStats.day_comparison.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-400">
                    Pas assez de données
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Period Comparison (Premium) */}
          <Card className="border-0 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <GitCompare className="h-5 w-5 text-purple-500" />
                Comparaison de périodes
                <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Premium</Badge>
              </CardTitle>
              <CardDescription>Comparez deux périodes pour identifier les tendances</CardDescription>
            </CardHeader>
            <CardContent>
              {restaurant?.subscription_plan !== 'premium' ? (
                <div className="text-center py-8">
                  <Crown className="h-12 w-12 text-amber-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Cette fonctionnalité nécessite un abonnement Premium</p>
                  <Button
                    onClick={() => window.location.href = '/billing'}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  >
                    Passer à Premium
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                    {/* Period 1 */}
                    <div className="flex-1 space-y-2">
                      <label className="text-sm font-medium text-gray-700">Période 1</label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(period1.from, 'dd MMM', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={period1.from}
                              onSelect={(date) => setPeriod1(prev => ({ ...prev, from: date }))}
                              locale={fr}
                            />
                          </PopoverContent>
                        </Popover>
                        <ArrowRight className="h-4 w-4 text-gray-400 self-center" />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(period1.to, 'dd MMM', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={period1.to}
                              onSelect={(date) => setPeriod1(prev => ({ ...prev, to: date }))}
                              locale={fr}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="text-lg font-bold text-gray-400 px-4 hidden sm:block">VS</div>

                    {/* Period 2 */}
                    <div className="flex-1 space-y-2">
                      <label className="text-sm font-medium text-gray-700">Période 2</label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(period2.from, 'dd MMM', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={period2.from}
                              onSelect={(date) => setPeriod2(prev => ({ ...prev, from: date }))}
                              locale={fr}
                            />
                          </PopoverContent>
                        </Popover>
                        <ArrowRight className="h-4 w-4 text-gray-400 self-center" />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="flex-1 justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(period2.to, 'dd MMM', { locale: fr })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={period2.to}
                              onSelect={(date) => setPeriod2(prev => ({ ...prev, to: date }))}
                              locale={fr}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <Button
                      onClick={fetchComparison}
                      disabled={comparisonLoading}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {comparisonLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Comparer'}
                    </Button>
                  </div>

                  {comparisonData && (
                    <div className="grid sm:grid-cols-3 gap-4 mt-6">
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-sm text-gray-500 mb-1">Scans QR</p>
                        <p className="text-2xl font-bold">{comparisonData.period1.stats.totals.scans}</p>
                        <p className={`text-sm ${comparisonData.changes.scans >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {comparisonData.changes.scans > 0 ? '+' : ''}{comparisonData.changes.scans}%
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-sm text-gray-500 mb-1">Vues plats</p>
                        <p className="text-2xl font-bold">{comparisonData.period1.stats.totals.views}</p>
                        <p className={`text-sm ${comparisonData.changes.views >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {comparisonData.changes.views > 0 ? '+' : ''}{comparisonData.changes.views}%
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl text-center">
                        <p className="text-sm text-gray-500 mb-1">Clics</p>
                        <p className="text-2xl font-bold">{comparisonData.period1.stats.totals.clicks}</p>
                        <p className={`text-sm ${comparisonData.changes.clicks >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {comparisonData.changes.clicks > 0 ? '+' : ''}{comparisonData.changes.clicks}%
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
